/**
 * Arbeitsschritte im Chat: deutsche Bezeichnungen der Werkzeuge und die Schrittliste,
 * die die Thinking-Timeline während des Wartens zeigt. Reine Logik, ohne React.
 */
import type { ThinkingEvent } from '../../../hooks/ai-chat-store';

/**
 * Human-friendly labels for the most common tools, keyed by tool name.
 * Anything unknown falls back to a humanized camelCase form.
 */
export const TOOL_LABELS: Record<string, { de: string; en: string }> = {
  // MCP-Werkzeuge (dieselben wie im MCP-Server, im Chat seit Oktober 2026)
  campaigns_list: { de: 'Kampagnen laden', en: 'Loading campaigns' },
  campaign_get: { de: 'Kampagne laden', en: 'Loading campaign' },
  campaign_stats: { de: 'Kampagnenzahlen laden', en: 'Loading campaign numbers' },
  campaign_update: { de: 'Kampagne ändern', en: 'Updating campaign' },
  campaign_set_status: { de: 'Kampagnenstatus ändern', en: 'Changing campaign status' },
  campaign_clone: { de: 'Kampagne klonen', en: 'Cloning campaign' },
  campaign_messages_list: { de: 'Nachrichten laden', en: 'Loading messages' },
  campaign_message_update: { de: 'Nachricht ändern', en: 'Updating message' },
  campaign_test: { de: 'Kampagne testen', en: 'Testing campaign' },
  campaign_test_result: { de: 'Testergebnis abholen', en: 'Fetching test result' },
  campaign_contacts_list: { de: 'Kontaktliste laden', en: 'Loading contact list' },
  campaign_contact_get: { de: 'Kontakt laden', en: 'Loading contact' },
  campaign_contacts_set_active: { de: 'Finale Liste anpassen', en: 'Updating final list' },
  campaign_contacts_unclear_gender: { de: 'Unklares Geschlecht prüfen', en: 'Checking unclear gender' },
  campaign_contacts_set_gender: { de: 'Geschlecht setzen', en: 'Setting gender' },
  campaign_readiness: { de: 'Startbereitschaft prüfen', en: 'Checking readiness' },
  campaign_searches_list: { de: 'Suchen laden', en: 'Loading searches' },
  contacts_search: { de: 'Kontakte suchen', en: 'Searching contacts' },
  contact_get: { de: 'Kontakt laden', en: 'Loading contact' },
  messages_pending_list: { de: 'Freigaben laden', en: 'Loading approvals' },
  message_approve: { de: 'Nachricht freigeben', en: 'Approving message' },
  message_reject: { de: 'Nachricht ablehnen', en: 'Rejecting message' },
  blacklists_list: { de: 'Blacklists laden', en: 'Loading blacklists' },
  blacklist_add: { de: 'Zur Blacklist hinzufügen', en: 'Adding to blacklist' },
  inbox_list: { de: 'Posteingang laden', en: 'Loading inbox' },
  inbox_messages: { de: 'Unterhaltung laden', en: 'Loading conversation' },
  inbox_reply: { de: 'Antwort senden', en: 'Sending reply' },
  analytics_summary: { de: 'Statistik laden', en: 'Loading statistics' },
  analytics_overview: { de: 'Übersicht laden', en: 'Loading overview' },
  linkedin_accounts_list: { de: 'LinkedIn-Accounts laden', en: 'Loading LinkedIn accounts' },
  linkedin_account_onboarding_status: { de: 'Login-Status prüfen', en: 'Checking login status' },
  listLinkedInAccounts: { de: 'LinkedIn-Accounts laden', en: 'Loading LinkedIn accounts' },
  checkLicenseAvailability: { de: 'Lizenzen prüfen', en: 'Checking licenses' },
  submitLinkedInCredentials: { de: 'LinkedIn-Login starten', en: 'Starting LinkedIn login' },
  submit2FACode: { de: '2FA-Code übermitteln', en: 'Submitting 2FA code' },
  pollAccountOnboarding: { de: 'Login-Status prüfen', en: 'Checking login status' },
  getLoginChallengeScreenshot: { de: 'Challenge-Screenshot laden', en: 'Loading challenge screenshot' },
  solveCaptchaClick: { de: 'Captcha lösen', en: 'Solving captcha' },
  createCampaign: { de: 'Kampagne anlegen', en: 'Creating campaign' },
  updateCampaignBriefing: { de: 'Briefing speichern', en: 'Saving briefing' },
  startAutomaticSearch: { de: 'Kontaktsuche starten', en: 'Starting contact search' },
  addContactsFromSearchUrl: { de: 'Kontakte importieren', en: 'Importing contacts' },
  copyContactsFromCampaign: { de: 'Kontakte kopieren', en: 'Copying contacts' },
  listMessageTemplates: { de: 'Vorlagen laden', en: 'Loading templates' },
  applyMessageTemplate: { de: 'Vorlage anwenden', en: 'Applying template' },
  setCampaignSteps: { de: 'Nachrichtenfluss speichern', en: 'Saving message flow' },
  previewCampaignMessages: { de: 'Vorschau generieren', en: 'Generating preview' },
  launchCampaign: { de: 'Kampagne starten', en: 'Launching campaign' },
  getWorkspaceOverview: { de: 'Überblick laden', en: 'Loading overview' },
  refreshInbox: { de: 'Posteingang aktualisieren', en: 'Refreshing inbox' },
  // Weitere MCP-Werkzeuge — ohne Eintrag stand hier „Analytics daily“
  analytics_daily: { de: 'Tagesverlauf laden', en: 'Loading daily figures' },
  analytics_steps: { de: 'Zahlen pro Schritt laden', en: 'Loading per-step figures' },
  analytics_conversion: { de: 'Quoten berechnen', en: 'Computing rates' },
  analytics_aggregated: { de: 'Zahlen über alle Kampagnen laden', en: 'Loading totals across campaigns' },
  analytics_comparison: { de: 'Kampagnen vergleichen', en: 'Comparing campaigns' },
  automation_health: { de: 'Automatisierung prüfen', en: 'Checking automation' },
  campaign_create: { de: 'Kampagne anlegen', en: 'Creating campaign' },
  campaign_delete: { de: 'Kampagne löschen', en: 'Deleting campaign' },
  campaign_briefing_update: { de: 'Briefing speichern', en: 'Saving briefing' },
  campaign_judging_set: { de: 'Bewertungskriterien speichern', en: 'Saving rating criteria' },
  campaign_ai_search: { de: 'KI-Suche starten', en: 'Starting AI search' },
  campaign_messages_set: { de: 'Nachrichten speichern', en: 'Saving messages' },
  campaign_placeholders_set: { de: 'Platzhalter speichern', en: 'Saving placeholders' },
  campaign_blacklists_set: { de: 'Blacklists zuordnen', en: 'Assigning blacklists' },
  campaign_contacts_add: { de: 'Kontakte hinzufügen', en: 'Adding contacts' },
  campaign_contacts_export: { de: 'Kontaktliste exportieren', en: 'Exporting contact list' },
  campaign_search_update: { de: 'Suche ändern', en: 'Updating search' },
  campaign_search_pause: { de: 'Suche pausieren', en: 'Pausing search' },
  campaign_search_resume: { de: 'Suche fortsetzen', en: 'Resuming search' },
  campaign_search_delete: { de: 'Suche löschen', en: 'Deleting search' },
  inbox_send: { de: 'Nachricht senden', en: 'Sending message' },
  inbox_mark_read: { de: 'Als gelesen markieren', en: 'Marking as read' },
  message_get: { de: 'Nachricht laden', en: 'Loading message' },
  messages_stats: { de: 'Freigabe-Zahlen laden', en: 'Loading approval figures' },
  messages_approve_bulk: { de: 'Nachrichten freigeben', en: 'Approving messages' },
  blacklist_check: { de: 'Blacklist prüfen', en: 'Checking blacklist' },
  blacklist_create: { de: 'Blacklist anlegen', en: 'Creating blacklist' },
  blacklist_items_list: { de: 'Blacklist-Einträge laden', en: 'Loading blacklist entries' },
  blacklist_remove_item: { de: 'Blacklist-Eintrag entfernen', en: 'Removing blacklist entry' },
  linkedin_account_settings_get: { de: 'Kontoeinstellungen laden', en: 'Loading account settings' },
  linkedin_account_worktime_set: { de: 'Arbeitszeiten speichern', en: 'Saving working hours' },
  linkedin_account_limits_set: { de: 'Limits speichern', en: 'Saving limits' },
  linkedin_account_signature_set: { de: 'Signatur speichern', en: 'Saving signature' },
  linkedin_account_quota: { de: 'Kontingent prüfen', en: 'Checking quota' },
  linkedin_account_retry_login: { de: 'Login erneut versuchen', en: 'Retrying login' },
  linkedin_account_challenge: { de: 'Sicherheitsprüfung laden', en: 'Loading security check' },
  linkedin_account_choose_contract: { de: 'Vertrag wählen', en: 'Choosing contract' },
  linkedin_post_create: { de: 'Beitrag veröffentlichen', en: 'Publishing post' },
  linkedin_post_delete: { de: 'Beitrag löschen', en: 'Deleting post' },
  // Häufige Chat-eigene Werkzeuge
  askUser: { de: 'Rückfrage vorbereiten', en: 'Preparing a question' },
  getCampaignSetupStatus: { de: 'Einrichtungsstand prüfen', en: 'Checking setup status' },
  confirmSearchesAndJudging: { de: 'Suche und Kriterien speichern', en: 'Saving search and criteria' },
  updateCampaignSettings: { de: 'Einstellungen speichern', en: 'Saving settings' },
  lookupLocations: { de: 'Orte nachschlagen', en: 'Looking up locations' },
  requestLinkedInCredentials: { de: 'Zugangsdaten-Formular öffnen', en: 'Opening credentials form' },
  openBlacklistDialog: { de: 'Blacklist-Dialog öffnen', en: 'Opening blacklist dialog' },
  getAccountLogs: { de: 'Kontoverlauf laden', en: 'Loading account history' },
  searchKnowledgeBase: { de: 'Wissen nachschlagen', en: 'Looking up help articles' },
};

/** "getWorkspaceOverview" → "Get workspace overview" */
function humanizeToolName(name: string): string {
  const spaced = name.replace(/_/g, ' ').replace(/([A-Z])/g, ' $1').replace(/\s+/g, ' ').trim().toLowerCase();
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

export function toolLabel(toolName: string, language: string): string {
  const entry = TOOL_LABELS[toolName];
  if (entry) return language.startsWith('de') ? entry.de : entry.en;
  return humanizeToolName(toolName);
}

/** Ein sichtbarer Arbeitsschritt: eine Zeile, laufend, erledigt oder fehlgeschlagen. */
export interface WorkStep {
  key: string;
  label: string;
  state: 'running' | 'done' | 'error';
}

/** Kurzer Zusatz aus den Werkzeug-Argumenten, z. B. der Suchbegriff. */
function argHint(step: ThinkingEvent): string {
  const args = (step.detail as { args?: Record<string, unknown> } | undefined)?.args;
  const q = args && typeof args.q === 'string' ? args.q.trim() : '';
  return q ? `: „${q.length > 40 ? `${q.slice(0, 40)}…` : q}“` : '';
}

/**
 * Aus den Server-Ereignissen (plan → tool_call → observe → decide je Werkzeug) wird
 * eine Zeile pro Arbeitsschritt. Vorher zeigte die Liste vier Ereignisse je Werkzeug,
 * davon drei Füllzeilen („Überlege den nächsten Schritt…“), und nur die letzten vier.
 */
export function buildWorkSteps(
  events: ThinkingEvent[],
  answering: boolean,
  label: (toolName: string) => string,
  texts: { understanding: string; answering: string },
): WorkStep[] {
  const steps: WorkStep[] = [{ key: 'understand', label: texts.understanding, state: events.length ? 'done' : 'running' }];
  for (const [index, event] of events.entries()) {
    if (event.stage === 'tool_call' && event.toolName) {
      steps.push({ key: `tool-${index}`, label: label(event.toolName) + argHint(event), state: 'running' });
    } else if (event.stage === 'observe' && event.toolName) {
      // Ergebnis zum letzten noch laufenden Schritt desselben Werkzeugs
      for (let i = steps.length - 1; i > 0; i--) {
        if (steps[i].state === 'running' && steps[i].label.startsWith(label(event.toolName))) {
          steps[i] = { ...steps[i], state: event.status === 'error' ? 'error' : 'done' };
          break;
        }
      }
    }
  }
  const stillRunning = steps.some((st) => st.state === 'running');
  if (events.length && (answering || !stillRunning)) {
    steps.push({ key: 'answer', label: texts.answering, state: 'running' });
  }
  return steps;
}

